import { useTranslation } from 'react-i18next';

import { useSelector } from 'hooks';

import type { Header, TabType } from '../types';

/**
 * Grant filtering and translated sorting for both navigation levels.
 *
 * Split out of `BaseTabsRouting` so that "no header survived" can be a real early
 * return there: everything below that point needs a header and a view to start
 * from - `useTabNavigation` is handed both, and the toolbar indexes into them.
 */
const useVisibleTabs = (headers: Header[], views: TabType[]) => {
  'use memo';

  const { t } = useTranslation('common');
  const grants = useSelector((state) => state.grants);

  const byTranslatedName = (a: TabType, b: TabType) =>
    t(`tableTabs.${a.name}`).localeCompare(t(`tableTabs.${b.name}`));

  //filter and sort by name
  const filteredHeaders = headers
    .filter(
      (header) =>
        !header.requiredGrants ||
        header.requiredGrants.every((requiredGrant) => {
          return grants[requiredGrant];
        })
    )
    .map((header) => ({
      ...header,
      tabs: header.tabs
        .filter(
          (tab) => !tab.grants || tab.grants.some((grant) => grants[grant])
        )
        .sort(byTranslatedName)
    }))
    .filter((header) => header.tabs.length > 0);

  // A view is only reachable through its header, so it inherits the header's
  // `requiredGrants` - which `TabType` itself does not carry. Without this, a view
  // whose header was filtered out stays selectable through the page param:
  // `useTabNavigation` resolves it, `findIndex` returns -1, and indexing
  // `filteredHeaders` with that takes down the whole screen. It would also still
  // get a route, and `ProtectedRoute` only checks the screen-level grant.
  const visibleHeaderNames = new Set(
    filteredHeaders.map(({ subHeaderName }) => subHeaderName)
  );

  const filteredViews = views
    .filter(
      (view) =>
        visibleHeaderNames.has(view.parent) &&
        (!view.grants || view.grants.some((grant) => grants[grant]))
    )
    .sort(byTranslatedName);

  return { filteredHeaders, filteredViews };
};

export default useVisibleTabs;
